







































































































import {
  SfButton,
  SfAlert
} from '@storefront-ui/vue';
import {
  ref,
  computed,
  useContext,
  useRoute,
  useRouter,
  defineComponent,
  PropType,
  onBeforeMount
} from '@nuxtjs/composition-api';

import { useCustomerPricing } from '@wemade-vsf/catalog/lib/runtime/product/composables/useCustomerPricing';
import { Product } from '@wemade-vsf/catalog/lib/runtime/product/types';
import { usePageStore } from '@wemade-vsf/core';
import ProductDetails from 'components/catalog/Product/Details/ProductDetails.vue';
import Swatch from 'components/catalog/Swatch.vue';
import ProductPrice from 'components/catalog/Product/ProductPrice.vue';
import ProductActions from 'components/catalog/Product/Details/ProductActions.vue';

export default defineComponent({
  name: 'ConfigurableProduct',
  components: {
    SfButton,
    ProductPrice,
    SfAlert,
    ProductDetails,
    Swatch,
    ProductActions,
    WmSelect: () => import('components/theme/Form/Select.vue'),
    Buttons: () => import('components/catalog/Product/ProductTypes/configurable/ConfigurableOptionType/Buttons.vue'),
    ProductOptions: () => import('components/catalog/Product/Details/ProductOptions/ProductOptions.vue')
  },
  transition: 'fade',
  props: {
    product: {
      type: [Object, null] as PropType<Product>,
      default: null,
    },
    isFetching: {
      type: Boolean,
      default: true,
    }
  },
  setup(props, { emit }) {
    const route = useRoute();
    const router = useRouter();
    const { routeData } = usePageStore();
    const { app } = useContext();
    const { getPriceForConfiguredVariant } = useCustomerPricing();

    const qty = ref(1)
    const variants = computed(() => props.product.variants 
      ? props.product.variants.map(variant => {
        let atts = {}
        variant?.attributes?.forEach(att => {
          if (att?.code && att?.uid) {
            atts[att.code] = att.uid
          }
        })
        return atts;
      })
      : [])

    const productConfiguration = ref(route.value.query);

    const configurableOptions = computed( () => {
        if (props.product?.configurable_options) {
          return props.product.configurable_options.map(curAttr => {
            const swatchOptions = curAttr?.values ? curAttr.values.filter(v => !!v?.swatch_data) : []
            return {
              attribute_uid: curAttr?.attribute_uid ?? '',
              attribute_code: curAttr?.attribute_code ?? '',
              label: curAttr?.label ?? '',
              values: curAttr?.values ?? [],
              position: curAttr?.position ?? 0,
              uid: curAttr?.uid ?? '',
              isSwatch: swatchOptions.length > 0
            }
          }).sort((a, b) => {
            return (a.position ?? 0) - (b.position ?? 0)
          })
        }
        return []
      }
    );

    const uidCodeMap = computed(() => props.product?.configurable_options?.reduce((prev, current) => {
      if (current?.attribute_code && current?.attribute_uid) {
        prev[current.attribute_uid] = current.attribute_code
      }
      return prev
    }, {}))

    const availableOptions = computed(() => {
      if (!props.product || !uidCodeMap.value) return {}
      let attributes = {};
      Object.keys(uidCodeMap.value).forEach(uid => {
        const code = uidCodeMap.value[uid];
        const otherAttributes = Object.keys(productConfiguration.value).filter(k => k !== uid);
        const possibleVariants = otherAttributes.length 
          ? variants.value.filter(variant => {
            return otherAttributes.every(attUid => variant[uidCodeMap.value[attUid]] === productConfiguration.value[attUid]);
          })
          : variants.value
        attributes[code] = []
        possibleVariants.forEach(variant => {
          const valueUid = variant[code];
          if (attributes[code].indexOf(valueUid) === -1) {
            attributes[code].push(valueUid)
          }
        })
      })
      return attributes
    })

    const configurableOptionsChooser = app.$wm.catalog.configurableOptionsChooser === 'buttons' ? 'buttons' : 'select';

    const productPrice = computed(() => getPriceForConfiguredVariant(props.product));

    const getBaseSearchQuery = () => ({
      filter: {
        sku: {
          eq: routeData.sku,
        },
      },
      configurations: Object.entries(productConfiguration.value).map(
        (config) => config[1],
      ) as string[],
    });

    const updateProductConfiguration = (label: string, value: string) => {
      if (productConfiguration.value[label] === value) {
        delete productConfiguration.value[label];
      } else {
        if (value) {
          productConfiguration.value[label] = value;
        } else {
          delete productConfiguration.value[label];
        }
      }
      const routerData = router.resolve({
        path: `${app.localePath(window.location.pathname)}`,
        query: {
          ...productConfiguration.value,
        },
      });

      window.history.pushState({}, '', routerData.href);
      emit('fetchProduct', { query: getBaseSearchQuery() });
    };

    onBeforeMount(() => {
      emit('fetchProduct', { query: getBaseSearchQuery() });
    })

    return {
      configurableOptions,
      updateProductConfiguration,
      productConfiguration,
      productPrice,
      qty,
      availableOptions,
      variants,
      configurableOptionsChooser
    };
  },
});
